import { forwardRef, memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { DEFAULT_MAX_HEIGHT } from './chat-input-composer.config'
import type { ChatInputComposerProps, ChatInputComposerRef } from './chat-input-composer.interface'
import { ChatInputComposerBody } from './chat-input-composer-body.atom'
import { useChatInputComposer } from './use-chat-input-composer.hook'

export type { ChatInputComposerProps, ChatInputComposerRef }

const getClassName = (isNoteMode: boolean, isDisabled: boolean, className?: string): string =>
  cn(
    'chat-input-composer flex flex-col rounded-2xl border bg-background shadow-sm transition-colors',
    'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
    isNoteMode && 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30',
    isDisabled && 'cursor-not-allowed opacity-50',
    className
  )

const ChatInputComposerBase = forwardRef<ChatInputComposerRef, ChatInputComposerProps>(
  (props, ref): ReactNode => {
    const { mode = 'message', attachmentList = [], maxHeight = DEFAULT_MAX_HEIGHT } = props
    const { isDisabled = false, isFileAttachEnabled = true, isLoading = false } = props
    const { isUploading = false, uploadProgress = 0, className } = props

    const { isNoteMode, bodyProps } = useChatInputComposer({
      value: props.value,
      onChange: props.onChange,
      mode,
      onModeChange: props.onModeChange,
      attachmentList,
      onFilesSelected: props.onFilesSelected,
      onAttachmentRemove: props.onAttachmentRemove,
      onAttachFromChats: props.onAttachFromChats,
      onSend: props.onSend,
      placeholder: props.placeholder,
      maxHeight,
      isDisabled,
      isFileAttachEnabled,
      isLoading,
      isStreaming: props.isStreaming,
      isStopping: props.isStopping,
      onStop: props.onStop,
      isUploading,
      uploadProgress,
      ref,
      templateList: props.templateList,
      onTemplateSelect: props.onTemplateSelect,
      onCreateTemplate: props.onCreateTemplate,
    })

    return (
      <div className={getClassName(isNoteMode, isDisabled, className)}>
        <ChatInputComposerBody {...bodyProps} />
      </div>
    )
  }
)

ChatInputComposerBase.displayName = 'ChatInputComposer'

export const ChatInputComposer = memo(ChatInputComposerBase)
